/**
 * @module useSignInFormLogin
 * @description Custom hook for handling sign-in form submission and authentication
 */

import { useState } from 'react';
import { useMutation } from '@apollo/client';
import useLocalStorage from './useLocalStorage';
import { LOGIN_MUTATION } from '../components/SignIn/operations';
import useTealiumTrigger from './useTealiumTrigger';

/**
 * @function triggerPasswordMigrationModal
 * @description Dispatches a custom event to open the password migration modal
 * @param {string} email - User's email address
 * @private
 */
const triggerPasswordMigrationModal = (email) => {
  document.dispatchEvent(
    new CustomEvent('passwordMigration:openModal', { detail: { email } }),
  );
};

/**
 * @function triggerForgotPasswordSuccess
 * @description Dispatches a custom event to handle successful login after password reset
 * @private
 */
const triggerForgotPasswordSuccess = () => {
  document.dispatchEvent(new CustomEvent('forgotPassword:loginSuccess'));
};

/**
 * @function useSignInFormLogin
 * @description Hook that provides sign-in functionality and error handling
 *
 * @param {Object} params - Configuration parameters
 * @param {Function} params.getPrimaryFormError - Function that returns the primary form error message
 * @param {Function} params.getValues - Function to retrieve form values (from React Hook Form)
 * @param {Function} [params.onFailedSubmission=()=>{}] - Callback for failed submission
 * @param {Function} [params.onSuccessfulSubmission=()=>{}] - Callback for successful submission
 * @param {Function} params.setError - Function to set form errors (from React Hook Form)
 *
 * @returns {Array} - Array containing:
 *   [0] {Function} runLogin - Function to execute the login mutation
 *   [1] {boolean} isLoyaltyConversionShown - Whether the loyalty conversion modal should be shown
 *   [2] {boolean} isChangePasswordModalOpen - Whether the change password modal should be shown
 *
 * @example
 * const { register, handleSubmit, setError, getValues } = useForm();
 * const [runLogin, isLoyaltyConversionShown] = useSignInFormLogin({
 *   getPrimaryFormError: () => "Login failed. Please try again.",
 *   getValues,
 *   setError,
 *   onSuccessfulSubmission: (data) => {
 *     navigate('/account');
 *   }
 * });
 *
 * const onSubmit = (data) => {
 *   runLogin({ variables: { email: data.email, password: data.password } });
 * };
 */
export default function useSignInFormLogin({
  getPrimaryFormError,
  getValues,
  onFailedSubmission = () => {},
  onSuccessfulSubmission = () => {},
  setError,
}) {
  const [isLoyaltyConversionShown, setIsLoyaltyConversionShown] = useState(false);
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
  const [, setIsRemembered] = useLocalStorage('isRemembered', false);
  const [tempPwdLoggedIn] = useLocalStorage('tempPwdLoggedIn', false);
  const trackInvalidEmail = useTealiumTrigger('universal', 'INVALID_EMAIL');
  const trackInvalidSignIn = useTealiumTrigger('universal', 'SIGN_IN_FAIL');

  /**
   * @function onSuccessfulLogin
   * @description Handles successful login response
   * @param {Object} loginRes - Login response data
   * @private
   */
  const onSuccessfulLogin = (loginRes) => {
    const { isPasswordUpdateRequired } = loginRes;

    /*
      Trigger new event to initialize and show the interupted change password flow

      See runLogin.js and forgotPassword.js within CRS. Introduce new event to trigger
      the two operations defined below:

      ```runLogin.js - onSuccess
        ...
        // Initiate forgot password flow with no event passed
        forgotPwd.initForgotPasswordModal();
        // Trigger page refresh if required, on forgot password modal closure
        $(ANF).trigger('forgotPassword:loginSuccess');
      ```
    */
    if (isPasswordUpdateRequired) triggerForgotPasswordSuccess();

    // Note: we may not need to do this, but numerous modules do rely on this value in CRS
    setIsRemembered(false);
    onSuccessfulSubmission(loginRes);
  };

  /**
   * @function onFailedLogin
   * @description Handles failed login response and sets appropriate errors
   * @param {Object} loginRes - Login response data containing errors
   * @private
   */
  const onFailedLogin = (loginRes) => {
    const { errors } = loginRes;
    const { message = {}, status } = errors?.find((err) => err?.message) || {};
    const { key = '' } = message;

    switch (key) {
      case '._ERR_USER_DOES_NOT_EXIST':
        setError('email', { message, status });
        setError('form', { message: getPrimaryFormError(), status });
        trackInvalidEmail();
        break;
      case '.ERR_TERMS_NOT_ACCEPTED':
        setIsLoyaltyConversionShown(true);
        break;
      case '.RESET_PW_USER_MIGRATED_FROM_SFCC':
        triggerPasswordMigrationModal(getValues('email'));
        break;
      case '._ERR_PASSWORD_EXPIRED':
      case '.2030': // Mismatch email/password
      case '.2490': // Account temporary lock
        setError('form', { message, status });
        trackInvalidSignIn();
        break;
      default:
        setError('form', { message: getPrimaryFormError(), status });
        trackInvalidSignIn();
        break;
    }

    onFailedSubmission(loginRes);
  };

  /**
   * @type {[Function, Object]}
   * Apollo mutation hook for login functionality
   */
  const [runLogin] = useMutation(LOGIN_MUTATION, {
    onCompleted: ({ login }) => {
      // TODO: Tealium events - to be handled in https://anfcorp.atlassian.net/browse/PRO-720
      const { success, isPasswordUpdateRequired } = login;

      if (isPasswordUpdateRequired && tempPwdLoggedIn) {
        setIsChangePasswordModalOpen(true);
        return;
      }
      if (success) {
        onSuccessfulLogin(login);
        return;
      }

      onFailedLogin(login);
    },
    onError: (err) => { onFailedSubmission(err); },
  });

  return [runLogin, isLoyaltyConversionShown, isChangePasswordModalOpen];
}
